import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatEURDecimal, formatDateFR } from "../../lib/api";
import { Download, CreditCard, Banknote, Link2, ShieldCheck, Wrench, ShoppingCart, FileText, TrendingUp, TrendingDown, Scale } from "lucide-react";
import SegmentedPills from "../ui/SegmentedPills";
import ViewToggle, { useViewMode } from "./ViewToggle";
import { PurchasesSection, FreeInvoiceForm } from "./BillingExtras";
import { usePermissions } from "../../lib/permissions";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

function useCanCreateBilling() {
  const { can } = usePermissions();
  return can("billing", "create");
}

// ─── Filtre de période (indépendant par sous-onglet) ─────────────────
export function PeriodFilter({ value, onChange, testid }) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <SegmentedPills
        items={[
          { key: "day", label: "Jour" }, { key: "week", label: "Semaine" },
          { key: "month", label: "Mois" }, { key: "year", label: "Année" },
          { key: "custom", label: "Personnalisée" },
        ]}
        value={value.mode}
        onChange={(m) => onChange({ ...value, mode: m })}
        testid={testid}
      />
      {value.mode === "custom" && (
        <div className="flex items-center gap-1.5">
          <input type="date" value={value.from || ""} onChange={(e) => onChange({ ...value, from: e.target.value })}
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs" data-testid={`${testid}-from`} />
          <span className="text-xs text-slate-400">→</span>
          <input type="date" value={value.to || ""} onChange={(e) => onChange({ ...value, to: e.target.value })}
            className="h-8 rounded-lg border border-slate-200 px-2 text-xs" data-testid={`${testid}-to`} />
        </div>
      )}
    </div>
  );
}

export const usePeriod = () => useState({ mode: "month", from: "", to: "" });

export function periodBounds(p) {
  const now = new Date();
  const sod = (d) => { const r = new Date(d); r.setHours(0, 0, 0, 0); return r; };
  if (p.mode === "custom") {
    return [p.from ? sod(new Date(p.from + "T00:00:00")) : new Date(0), p.to ? new Date(p.to + "T23:59:59") : now];
  }
  let start = sod(now);
  if (p.mode === "week") start.setDate(start.getDate() - ((start.getDay() + 6) % 7));
  else if (p.mode === "month") start.setDate(1);
  else if (p.mode === "year") start.setMonth(0, 1);
  return [start, now];
}

export const inPeriod = (dateStr, p) => {
  if (!dateStr) return false;
  const [s, e] = periodBounds(p);
  const d = new Date(dateStr);
  return d >= s && d <= e;
};

export const PERIOD_LABEL = { day: "aujourd'hui", week: "cette semaine", month: "ce mois", year: "cette année", custom: "période personnalisée" };

const csvEscape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
export function exportCsv(filename, headers, rows) {
  const csv = [headers.map(csvEscape).join(";"), ...rows.map((r) => r.map(csvEscape).join(";"))].join("\n");
  const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

// ─── 📊 Synthèse ──────────────────────────────────────────────────────
const PAYMENT_METHOD_LABEL = {
  stripe: "Stripe", cb: "Carte bancaire", especes: "Espèces", cheque: "Chèque",
  virement: "Virement", prelevement: "Prélèvement", autre: "Autre",
};
const payLabel = (m) => PAYMENT_METHOD_LABEL[m] || (m ? m : "Stripe");
const n2 = (v) => (v == null || v === "" ? "" : Number(v).toFixed(2).replace(".", ","));

export function SyntheseView({ stats, bookings, transactions, invoices, purchases, maintenance, creditNotes }) {
  const [period, setPeriod] = usePeriod();
  const byBooking = useMemo(() => Object.fromEntries(bookings.map((b) => [b.id, b])), [bookings]);

  const k = useMemo(() => {
    const txs = transactions.filter((t) => inPeriod(t.paid_at || t.created_at, period));
    const encaisse = txs.filter((t) => t.type !== "caution").reduce((a, t) => a + (t.amount || 0), 0);
    const bks = bookings.filter((b) => inPeriod(b.created_at, period));
    const net = bks.reduce((a, b) => a + (b.vendor_revenue || 0), 0);
    const invs = invoices.filter((i) => i.status === "issued");
    const pendingInvoices = invs.reduce((a, i) => a + (i.balance_due ?? i.total_ttc ?? 0), 0);
    const cautionsHeld = bookings
      .filter((b) => b.caution_status === "held")
      .reduce((a, b) => a + (b.caution_paid_amount || b.caution_amount || 0), 0);
    const dep = purchases.filter((p) => inPeriod(p.date || p.created_at, period)).reduce((a, p) => a + (p.amount_ttc || 0), 0)
      + maintenance.filter((m) => inPeriod(m.date || m.created_at, period)).reduce((a, m) => a + (m.cost || 0), 0);
    const byType = {};
    txs.forEach((t) => { if (t.type !== "caution") byType[t.type || "shop_checkout"] = (byType[t.type || "shop_checkout"] || 0) + (t.amount || 0); });
    return { encaisse, net, pendingInvoices, pendingCount: invs.length, cautionsHeld, dep, solde: encaisse - dep, txCount: txs.length, bkCount: bks.length, byType };
  }, [transactions, bookings, invoices, purchases, maintenance, period]);

  const doExportComptable = () => {
    const [s, e] = periodBounds(period);
    const iso = (d) => d.toISOString().slice(0, 10);
    const headers = ["Date", "Type", "Référence", "Client / Fournisseur", "Libellé", "Mode de règlement", "Statut", "Sens", "Montant HT", "TVA", "Montant TTC"];
    const rows = [];

    transactions.filter((t) => inPeriod(t.paid_at || t.created_at, period)).forEach((t) => {
      const b = byBooking[t.booking_id];
      rows.push({
        date: (t.paid_at || t.created_at || "").slice(0, 10),
        cells: [
          t.type === "caution" ? "Encaissement caution" : "Encaissement",
          b?.reference || t.booking_id || "",
          t.client_name || b?.client_name || "",
          t.type === "caution" ? "Dépôt de garantie" : `Location ${b?.vehicle_name || ""}`.trim(),
          payLabel(t.method),
          "Payé", "Crédit", "", "", n2(t.amount),
        ],
      });
    });

    invoices.filter((i) => inPeriod(i.issue_date || i.created_at, period)).forEach((i) => {
      rows.push({
        date: i.issue_date || (i.created_at || "").slice(0, 10),
        cells: [
          "Facture", i.reference, i.client?.name || "",
          i.kind === "free" ? "Facture libre" : `Location ${i.vehicle_name || ""}`.trim(),
          "", { issued: "Émise", paid: "Payée", cancelled: "Annulée" }[i.status] || i.status,
          "Crédit", n2(i.total_ht), n2(i.vat_amount), n2(i.total_ttc),
        ],
      });
    });

    creditNotes.filter((c) => inPeriod(c.issue_date || c.created_at, period)).forEach((c) => {
      rows.push({
        date: c.issue_date || (c.created_at || "").slice(0, 10),
        cells: [
          "Avoir", c.reference, c.client?.name || "",
          `${c.reason || "Avoir"} (sur ${c.invoice_reference || "facture"})`,
          "", "Émis", "Débit", n2(c.amount_ht), n2(c.vat_amount), n2(c.amount_ttc),
        ],
      });
    });

    purchases.filter((p) => inPeriod(p.date || p.created_at, period)).forEach((p) => {
      const ht = p.amount_ht;
      rows.push({
        date: p.date || (p.created_at || "").slice(0, 10),
        cells: [
          "Achat fournisseur", p.reference || "", p.supplier_name || "", p.label || "",
          payLabel(p.payment_method), p.status === "paid" ? "Payé" : "À payer",
          "Débit", n2(ht), ht != null && p.amount_ttc != null ? n2(p.amount_ttc - ht) : "", n2(p.amount_ttc),
        ],
      });
    });

    maintenance.filter((m) => inPeriod(m.date || m.created_at, period)).forEach((m) => {
      rows.push({
        date: m.date || (m.created_at || "").slice(0, 10),
        cells: [
          "Maintenance", m.type || "entretien", m.provider || m.vehicle_name || "",
          `${m.title || "Intervention"}${m.vehicle_name ? ` · ${m.vehicle_name}` : ""}`,
          "", "", "Débit", "", "", n2(m.cost),
        ],
      });
    });

    rows.sort((a, b) => (a.date || "").localeCompare(b.date || ""));
    const data = rows.map((r) => [r.date, ...r.cells]);

    const vatCollected = invoices.filter((i) => i.status !== "cancelled" && inPeriod(i.issue_date || i.created_at, period)).reduce((a, i) => a + (i.vat_amount || 0), 0)
      - creditNotes.filter((c) => inPeriod(c.issue_date || c.created_at, period)).reduce((a, c) => a + (c.vat_amount || 0), 0);
    data.push([], ["TOTAUX", "", "", "", "", "", "", "", "", "", ""]);
    data.push(["", "Total encaissements (hors cautions)", "", "", "", "", "", "Crédit", "", "", n2(k.encaisse)]);
    data.push(["", "TVA collectée (factures − avoirs)", "", "", "", "", "", "", "", n2(vatCollected), ""]);
    data.push(["", "Total dépenses (achats + maintenance)", "", "", "", "", "", "Débit", "", "", n2(k.dep)]);

    exportCsv(`export-comptable_${iso(s)}_${iso(e)}.csv`, headers, data);
  };

  return (
    <div data-testid="revenue-synthese">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodFilter value={period} onChange={setPeriod} testid="synth-period" />
        <button onClick={doExportComptable} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-400" data-testid="synth-export-comptable">
          <Download className="h-3.5 w-3.5" /> Export comptable (CSV)
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
        <KpiTile label="Encaissé" value={formatEURDecimal(k.encaisse)} icon={TrendingUp} hint={`${k.txCount} règlement${k.txCount > 1 ? "s" : ""} · ${PERIOD_LABEL[period.mode]}`} tone="success" testid="synth-kpi-encaisse" />
        <KpiTile label="Revenu net réservations" value={formatEURDecimal(k.net)} icon={Banknote} hint={`${k.bkCount} réservation${k.bkCount > 1 ? "s" : ""} sur la période`} tone="primary" testid="synth-kpi-net" />
        <KpiTile label="Factures en attente" value={formatEURDecimal(k.pendingInvoices)} icon={FileText} hint={`${k.pendingCount} facture${k.pendingCount > 1 ? "s" : ""} émise${k.pendingCount > 1 ? "s" : ""} non payée${k.pendingCount > 1 ? "s" : ""}`} tone="warning" testid="synth-kpi-invoices" />
        <KpiTile label="Cautions immobilisées" value={formatEURDecimal(k.cautionsHeld)} icon={ShieldCheck} hint="Montants actuellement retenus" tone="neutral" testid="synth-kpi-cautions" />
        <KpiTile label="Dépenses" value={formatEURDecimal(k.dep)} icon={TrendingDown} hint="Achats fournisseurs + maintenance" tone="danger" testid="synth-kpi-depenses" />
        <KpiTile label="Solde de trésorerie" value={formatEURDecimal(k.solde)} icon={Scale} hint="Encaissé − dépenses sur la période" tone={k.solde >= 0 ? "success" : "danger"} testid="synth-kpi-solde" />
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mt-6">
        {stats?.revenue_chart?.length > 0 && (
          <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200 p-6" data-testid="synth-chart">
            <div className="overline">Évolution</div>
            <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">Revenus nets par mois</h3>
            <div className="h-64 mt-4" style={{ minHeight: 256 }}>
              <ResponsiveContainer width="100%" height={256}>
                <BarChart data={stats.revenue_chart}>
                  <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="month" stroke="#94a3b8" fontSize={11} />
                  <YAxis stroke="#94a3b8" fontSize={11} tickFormatter={(v) => `${Math.round(v)}€`} />
                  <Tooltip contentStyle={{ background: "#0f172a", border: "none", borderRadius: 12, color: "white", fontSize: 12 }} formatter={(v) => formatEURDecimal(v)} />
                  <Bar dataKey="revenue" fill="#7c3aed" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
        <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="synth-breakdown">
          <div className="overline">Répartition</div>
          <h3 className="font-heading text-xl font-medium tracking-tight text-slate-900">Encaissements par canal</h3>
          <div className="mt-4 space-y-3 text-sm">
            {Object.keys(k.byType).length === 0 ? (
              <div className="text-slate-400 text-sm py-6 text-center">Aucun encaissement sur la période.</div>
            ) : (
              Object.entries(k.byType).map(([type, amount]) => {
                const m = TX_META[type] || TX_META.shop_checkout;
                return (
                  <div key={type} className="flex items-center justify-between">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                    <span className="font-semibold text-slate-900">{formatEURDecimal(amount)}</span>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── 💳 Encaissements ─────────────────────────────────────────────────
const TX_META = {
  shop_checkout: { label: "Boutique", icon: CreditCard, cls: "bg-violet-100 text-violet-700" },
  booking_payment: { label: "Lien de paiement", icon: Link2, cls: "bg-blue-100 text-blue-700" },
  manual_payment: { label: "Paiement manuel", icon: Banknote, cls: "bg-emerald-100 text-emerald-700" },
  caution: { label: "Caution", icon: ShieldCheck, cls: "bg-amber-100 text-amber-700" },
};

export function EncaissementsView({ transactions, bookings }) {
  const [period, setPeriod] = usePeriod();
  const [typeFilter, setTypeFilter] = useState("all");
  const [view, setView] = useViewMode("rev_encaissements", "list");
  const byBooking = useMemo(() => Object.fromEntries(bookings.map((b) => [b.id, b])), [bookings]);

  const filtered = useMemo(() => transactions.filter((t) =>
    inPeriod(t.paid_at || t.created_at, period) && (typeFilter === "all" || (t.type || "shop_checkout") === typeFilter)
  ), [transactions, period, typeFilter]);

  const totals = useMemo(() => ({
    total: filtered.filter((t) => t.type !== "caution").reduce((a, t) => a + (t.amount || 0), 0),
    commission: filtered.reduce((a, t) => a + (t.commission || (byBooking[t.booking_id]?.commission && t.type === "shop_checkout" ? byBooking[t.booking_id].commission : 0) || 0), 0),
    count: filtered.length,
  }), [filtered, byBooking]);

  const doExport = () => exportCsv(
    "encaissements.csv",
    ["Date", "Type", "Client", "Réservation", "Méthode", "Montant", "Commission Junago"],
    filtered.map((t) => [
      (t.paid_at || t.created_at || "").slice(0, 10),
      TX_META[t.type]?.label || t.type || "Boutique",
      t.client_name || byBooking[t.booking_id]?.client_name || "",
      byBooking[t.booking_id]?.reference || t.booking_id || "",
      t.method || "stripe",
      (t.amount || 0).toFixed(2),
      (t.commission || 0).toFixed(2),
    ])
  );

  return (
    <div data-testid="revenue-encaissements">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodFilter value={period} onChange={setPeriod} testid="enc-period" />
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedPills
            items={[{ key: "all", label: "Tous" }, { key: "shop_checkout", label: "Boutique" }, { key: "booking_payment", label: "Liens" }, { key: "manual_payment", label: "Manuels" }, { key: "caution", label: "Cautions" }]}
            value={typeFilter} onChange={setTypeFilter} testid="enc-type-filter"
          />
          <ViewToggle value={view} onChange={setView} testid="enc-view" />
          <button onClick={doExport} className="inline-flex items-center gap-1.5 h-9 px-3 rounded-xl border border-slate-200 bg-white text-xs font-semibold text-slate-600 hover:border-slate-400" data-testid="enc-export-csv">
            <Download className="h-3.5 w-3.5" /> Export CSV
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
        <MiniKpi label="Encaissé sur la période" value={formatEURDecimal(totals.total)} tone="emerald" testid="enc-kpi-total" />
        <MiniKpi label="Commission Junago" value={formatEURDecimal(totals.commission)} tone="violet" testid="enc-kpi-commission" />
        <MiniKpi label="Règlements" value={totals.count} tone="slate" testid="enc-kpi-count" />
      </div>

      {filtered.length === 0 ? (
        <Empty text="Aucun encaissement sur la période." testid="enc-empty" />
      ) : view === "cards" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {filtered.map((t) => {
            const m = TX_META[t.type] || TX_META.shop_checkout;
            const b = byBooking[t.booking_id];
            return (
              <div key={t.id || t.session_id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="enc-card">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                  <span className="text-[11px] text-slate-400">{formatDateFR((t.paid_at || t.created_at || "").slice(0, 10))}</span>
                </div>
                <div className="font-heading text-xl font-medium mt-2">{formatEURDecimal(t.amount)}</div>
                <div className="text-xs text-slate-500 truncate mt-0.5">{t.client_name || b?.client_name || "—"} {b?.reference ? `· ${b.reference}` : ""}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{t.method === "stripe" || !t.method ? "Stripe" : t.method}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <Table headers={["Date", "Type", "Client", "Réservation", "Méthode", "Montant"]} testid="enc-table">
          {filtered.map((t) => {
            const m = TX_META[t.type] || TX_META.shop_checkout;
            const b = byBooking[t.booking_id];
            return (
              <tr key={t.id || t.session_id} data-testid="enc-row">
                <td className="px-5 py-3 whitespace-nowrap">{formatDateFR((t.paid_at || t.created_at || "").slice(0, 10))}</td>
                <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span></td>
                <td className="px-5 py-3 max-w-[180px] truncate">{t.client_name || b?.client_name || "—"}</td>
                <td className="px-5 py-3 text-slate-500">{b?.reference || "—"}</td>
                <td className="px-5 py-3 text-slate-500">{t.method === "stripe" || !t.method ? "Stripe" : t.method}</td>
                <td className="px-5 py-3 text-right font-semibold">{formatEURDecimal(t.amount)}</td>
              </tr>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ─── 🛡️ Cautions ─────────────────────────────────────────────────────
const CAUTION_META = {
  link_sent: { label: "En attente", cls: "bg-blue-100 text-blue-700" },
  held: { label: "Immobilisée", cls: "bg-amber-100 text-amber-700" },
  captured: { label: "Capturée", cls: "bg-red-100 text-red-700" },
  released: { label: "Restituée", cls: "bg-emerald-100 text-emerald-700" },
};

export function CautionsView({ bookings }) {
  const navigate = useNavigate();
  const [period, setPeriod] = usePeriod();
  const [view, setView] = useViewMode("rev_cautions", "list");
  const list = useMemo(() => bookings.filter((b) =>
    b.caution_status && b.caution_status !== "none" && inPeriod(b.caution_held_at || b.created_at, period)
  ), [bookings, period]);

  const k = useMemo(() => ({
    held: list.filter((b) => b.caution_status === "held").reduce((a, b) => a + (b.caution_paid_amount || b.caution_amount || 0), 0),
    captured: list.filter((b) => b.caution_status === "captured").reduce((a, b) => a + (b.caution_captured_amount || b.caution_amount || 0), 0),
    released: list.filter((b) => b.caution_status === "released").length,
    pending: list.filter((b) => b.caution_status === "link_sent").length,
  }), [list]);

  const open = (b) => navigate(`/vendor/pilotage?tab=bookings&booking=${b.id}`);

  return (
    <div data-testid="revenue-cautions">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodFilter value={period} onChange={setPeriod} testid="caution-period" />
        <ViewToggle value={view} onChange={setView} testid="caution-view" />
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-4">
        <MiniKpi label="Immobilisées" value={formatEURDecimal(k.held)} tone="amber" testid="caution-kpi-held" />
        <MiniKpi label="Capturées" value={formatEURDecimal(k.captured)} tone="red" testid="caution-kpi-captured" />
        <MiniKpi label="Restituées" value={k.released} tone="emerald" testid="caution-kpi-released" />
        <MiniKpi label="En attente" value={k.pending} tone="blue" testid="caution-kpi-pending" />
      </div>
      {list.length === 0 ? (
        <Empty text="Aucune caution sur la période." testid="caution-empty" />
      ) : view === "cards" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {list.map((b) => {
            const m = CAUTION_META[b.caution_status] || CAUTION_META.link_sent;
            return (
              <button key={b.id} onClick={() => open(b)} className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-violet-300 transition-colors" data-testid="caution-card">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                  {b.caution_mode === "preauth" && <span className="px-1.5 py-0.5 rounded bg-slate-100 text-slate-500 text-[9px] font-bold uppercase">Empreinte</span>}
                </div>
                <div className="font-heading text-xl font-medium mt-2">{formatEURDecimal(b.caution_amount)}</div>
                <div className="text-xs text-slate-600 truncate mt-0.5">{b.client_name} · {b.vehicle_name}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">{b.reference || b.id.slice(0, 8).toUpperCase()} · {formatDateFR((b.caution_held_at || b.created_at || "").slice(0, 10))}</div>
              </button>
            );
          })}
        </div>
      ) : (
        <Table headers={["Client", "Réservation", "Véhicule", "Date", "Mode", "Montant", "Statut"]} testid="caution-table">
          {list.map((b) => {
            const m = CAUTION_META[b.caution_status] || CAUTION_META.link_sent;
            return (
              <tr key={b.id} onClick={() => open(b)} className="cursor-pointer hover:bg-violet-50/40" data-testid="caution-row">
                <td className="px-5 py-3 max-w-[160px] truncate">{b.client_name}</td>
                <td className="px-5 py-3 text-slate-500">{b.reference || b.id.slice(0, 8).toUpperCase()}</td>
                <td className="px-5 py-3 max-w-[160px] truncate">{b.vehicle_name}</td>
                <td className="px-5 py-3 whitespace-nowrap">{formatDateFR((b.caution_held_at || b.created_at || "").slice(0, 10))}</td>
                <td className="px-5 py-3 text-slate-500">{b.caution_mode === "preauth" ? "Empreinte" : "Encaissée"}</td>
                <td className="px-5 py-3 text-right font-semibold">{formatEURDecimal(b.caution_amount)}</td>
                <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span></td>
              </tr>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ─── 🧾 Factures ──────────────────────────────────────────────────────
const INVOICE_STATUS_META = {
  issued: { label: "Émise", cls: "bg-blue-100 text-blue-700" },
  paid: { label: "Payée", cls: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Annulée", cls: "bg-red-100 text-red-700" },
};

export function FacturesView({ invoices, onReload }) {
  const navigate = useNavigate();
  const canCreateBilling = useCanCreateBilling();
  const [period, setPeriod] = usePeriod();
  const [statusFilter, setStatusFilter] = useState("all");
  const [view, setView] = useViewMode("rev_factures", "list");

  const inWindow = useMemo(() => invoices.filter((i) => inPeriod(i.issue_date || i.created_at, period)), [invoices, period]);

  const totals = useMemo(() => ({
    collected: inWindow.filter((i) => i.status === "paid").reduce((a, i) => a + (i.total_ttc || 0), 0),
    pending: inWindow.filter((i) => i.status === "issued").reduce((a, i) => a + (i.balance_due ?? i.total_ttc ?? 0), 0),
    issuedCount: inWindow.filter((i) => i.status === "issued").length,
    paidCount: inWindow.filter((i) => i.status === "paid").length,
  }), [inWindow]);

  const filtered = useMemo(() =>
    statusFilter === "all" ? inWindow : inWindow.filter((i) => i.status === statusFilter),
  [inWindow, statusFilter]);

  const openInvoice = (inv) =>
    navigate(inv.booking_id
      ? `/vendor/pilotage?tab=bookings&booking=${inv.booking_id}&invoice=${inv.id}`
      : `/vendor/pilotage?tab=bookings&invoice=${inv.id}`);

  return (
    <div data-testid="revenue-factures">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodFilter value={period} onChange={setPeriod} testid="fact-period" />
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedPills
            value={statusFilter} onChange={setStatusFilter}
            items={[{ key: "all", label: "Toutes" }, { key: "issued", label: "Émises" }, { key: "paid", label: "Payées" }, { key: "cancelled", label: "Annulées" }]}
            testid="invoices-status-filter"
          />
          <ViewToggle value={view} onChange={setView} testid="fact-view" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 mt-4 max-w-md">
        <div className="rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-3" data-testid="invoices-total-collected">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">Encaissé</div>
          <div className="font-heading text-xl font-medium text-emerald-700 mt-0.5">{formatEURDecimal(totals.collected)}</div>
          <div className="text-[11px] text-slate-500">{totals.paidCount} facture{totals.paidCount > 1 ? "s" : ""} payée{totals.paidCount > 1 ? "s" : ""}</div>
        </div>
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3" data-testid="invoices-total-pending">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">En attente</div>
          <div className="font-heading text-xl font-medium text-amber-700 mt-0.5">{formatEURDecimal(totals.pending)}</div>
          <div className="text-[11px] text-slate-500">{totals.issuedCount} facture{totals.issuedCount > 1 ? "s" : ""} émise{totals.issuedCount > 1 ? "s" : ""}</div>
        </div>
      </div>

      <div className="mt-4">
        {canCreateBilling && <FreeInvoiceForm onCreated={(inv) => { onReload(); openInvoice(inv); }} />}
      </div>

      {filtered.length === 0 ? (
        <Empty text={invoices.length === 0 ? "Aucune facture. Générez-en une depuis une fiche réservation." : "Aucune facture pour ce filtre / cette période."} testid="invoices-empty" />
      ) : view === "cards" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {filtered.map((inv) => {
            const m = INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.issued;
            return (
              <button key={inv.id} onClick={() => openInvoice(inv)} className="text-left rounded-2xl border border-slate-200 bg-white p-4 hover:border-violet-300 transition-colors" data-testid={`invoice-card-${inv.reference}`}>
                <div className="flex items-center justify-between">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900 text-sm">
                    <FileText className="h-3.5 w-3.5 text-violet-600" /> {inv.reference}
                  </span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                </div>
                <div className="font-heading text-xl font-medium mt-2">{formatEURDecimal(inv.total_ttc)}</div>
                <div className="text-xs text-slate-600 truncate mt-0.5">{inv.client?.name || "—"}</div>
                <div className="text-[11px] text-slate-400 mt-0.5">Émise le {formatDateFR(inv.issue_date)} · Échéance {formatDateFR(inv.due_date)}</div>
                <div className="mt-1.5 flex gap-1.5">
                  {inv.kind === "free" && <span className="px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[9px] font-bold uppercase">Libre</span>}
                  {(inv.credited_total || 0) > 0 && <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">Avoir</span>}
                </div>
              </button>
            );
          })}
        </div>
      ) : (
        <Table headers={["Référence", "Émise le", "Client", "Échéance", "Statut", "Total TTC"]} testid="fact-table">
          {filtered.map((inv) => {
            const m = INVOICE_STATUS_META[inv.status] || INVOICE_STATUS_META.issued;
            return (
              <tr key={inv.id} onClick={() => openInvoice(inv)} className="cursor-pointer hover:bg-violet-50/40 transition-colors" data-testid={`invoice-row-${inv.reference}`}>
                <td className="px-5 py-3 whitespace-nowrap">
                  <span className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                    <FileText className="h-3.5 w-3.5 text-violet-600" /> {inv.reference}
                  </span>
                  {inv.kind === "free" && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-sky-100 text-sky-700 text-[9px] font-bold uppercase">Libre</span>}
                  {(inv.credited_total || 0) > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold" title={`Avoirs : ${inv.credited_total} €`}>Avoir</span>}
                </td>
                <td className="px-5 py-3 whitespace-nowrap text-slate-600">{formatDateFR(inv.issue_date)}</td>
                <td className="px-5 py-3 max-w-[200px] truncate">{inv.client?.name}</td>
                <td className="px-5 py-3 whitespace-nowrap text-slate-600">{formatDateFR(inv.due_date)}</td>
                <td className="px-5 py-3 whitespace-nowrap"><span className={`px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.cls}`}>{m.label}</span></td>
                <td className="px-5 py-3 text-right font-semibold text-slate-900">{formatEURDecimal(inv.total_ttc)}</td>
              </tr>
            );
          })}
        </Table>
      )}
    </div>
  );
}

// ─── 💸 Dépenses ──────────────────────────────────────────────────────
export function DepensesView({ purchases, maintenance, onChanged }) {
  const [period, setPeriod] = usePeriod();
  const [view, setView] = useViewMode("rev_depenses", "list");
  const canCreateBilling = useCanCreateBilling();

  const rows = useMemo(() => {
    const p = purchases
      .filter((x) => inPeriod(x.date || x.created_at, period))
      .map((x) => ({ id: x.id, kind: "achat", ref: x.reference, label: x.label, who: x.supplier_name || "—", date: x.date || (x.created_at || "").slice(0, 10), amount: x.amount_ttc || 0, status: x.status }));
    const m = maintenance
      .filter((x) => inPeriod(x.date || x.created_at, period))
      .map((x) => ({ id: x.id, kind: "maintenance", ref: x.type || "entretien", label: x.title || "—", who: x.vehicle_name || "—", date: x.date || (x.created_at || "").slice(0, 10), amount: x.cost || 0 }));
    return [...p, ...m].sort((a, b) => (b.date || "").localeCompare(a.date || ""));
  }, [purchases, maintenance, period]);

  const totPurchases = rows.filter((r) => r.kind === "achat").reduce((a, r) => a + r.amount, 0);
  const totMaint = rows.filter((r) => r.kind === "maintenance").reduce((a, r) => a + r.amount, 0);

  const KIND_META = {
    achat: { label: "Achat", icon: ShoppingCart, cls: "bg-violet-100 text-violet-700" },
    maintenance: { label: "Maintenance", icon: Wrench, cls: "bg-amber-100 text-amber-700" },
  };

  return (
    <div data-testid="revenue-depenses">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <PeriodFilter value={period} onChange={setPeriod} testid="dep-period" />
        <ViewToggle value={view} onChange={setView} testid="dep-view" />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4">
        <MiniKpi label="Achats fournisseurs" value={formatEURDecimal(totPurchases)} tone="violet" icon={ShoppingCart} testid="dep-kpi-purchases" />
        <MiniKpi label="Maintenance & interventions" value={formatEURDecimal(totMaint)} tone="amber" icon={Wrench} testid="dep-kpi-maintenance" />
        <MiniKpi label="Total dépenses" value={formatEURDecimal(totPurchases + totMaint)} tone="red" testid="dep-kpi-total" />
      </div>

      {rows.length === 0 ? (
        <Empty text="Aucune dépense sur la période." testid="dep-empty" />
      ) : view === "cards" ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {rows.map((r) => {
            const m = KIND_META[r.kind];
            return (
              <div key={`${r.kind}-${r.id}`} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="dep-card">
                <div className="flex items-center justify-between">
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span>
                  <span className="text-[11px] text-slate-400">{formatDateFR(r.date)}</span>
                </div>
                <div className="font-heading text-xl font-medium mt-2">{formatEURDecimal(r.amount)}</div>
                <div className="text-xs text-slate-600 truncate mt-0.5">{r.label}</div>
                <div className="text-[11px] text-slate-400 mt-0.5 truncate">{r.who} · {r.ref}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <Table headers={["Type", "Réf.", "Libellé", "Fournisseur / Véhicule", "Date", "Montant"]} testid="dep-table">
          {rows.map((r) => {
            const m = KIND_META[r.kind];
            return (
              <tr key={`${r.kind}-${r.id}`} data-testid="dep-row">
                <td className="px-5 py-3"><span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${m.cls}`}>{m.label}</span></td>
                <td className="px-5 py-3 text-slate-500">{r.ref || "—"}</td>
                <td className="px-5 py-3 max-w-[220px] truncate">{r.label}</td>
                <td className="px-5 py-3 max-w-[180px] truncate">{r.who}</td>
                <td className="px-5 py-3 whitespace-nowrap">{formatDateFR(r.date)}</td>
                <td className="px-5 py-3 text-right font-semibold">{formatEURDecimal(r.amount)}</td>
              </tr>
            );
          })}
        </Table>
      )}

      <div className="mt-6">
        {canCreateBilling ? <PurchasesSection onChanged={onChanged} /> : null}
      </div>
    </div>
  );
}

// ─── UI partagés ──────────────────────────────────────────────────────
export function KpiTile({ label, value, hint, icon: Icon, tone = "neutral", testid }) {
  const toneCls = {
    neutral: "bg-slate-50 border-slate-200",
    primary: "bg-gradient-to-br from-violet-50 to-white border-violet-200",
    success: "bg-emerald-50 border-emerald-200",
    warning: "bg-amber-50 border-amber-200",
    danger: "bg-red-50 border-red-200",
  }[tone];
  const iconCls = {
    neutral: "text-slate-500", primary: "text-violet-600", success: "text-emerald-600",
    warning: "text-amber-600", danger: "text-red-600",
  }[tone];
  const valueCls = {
    neutral: "text-slate-900", primary: "text-slate-900", success: "text-emerald-700",
    warning: "text-slate-900", danger: "text-red-700",
  }[tone];
  return (
    <div className={`rounded-2xl border p-4 ${toneCls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${iconCls}`} />}
      </div>
      <div className={`font-heading text-xl sm:text-2xl lg:text-[26px] font-medium tracking-tight mt-2 ${valueCls}`}>{value}</div>
      {hint && <div className="text-[11px] text-slate-500 mt-1 leading-snug">{hint}</div>}
    </div>
  );
}

export function MiniKpi({ label, value, tone = "slate", icon: Icon, testid }) {
  const cls = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-700",
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    amber: "bg-amber-50 border-amber-200 text-amber-700",
    red: "bg-red-50 border-red-200 text-red-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    slate: "bg-slate-50 border-slate-200 text-slate-800",
  }[tone];
  return (
    <div className={`rounded-2xl border px-4 py-3 ${cls}`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] tracking-[0.15em] uppercase font-bold text-slate-500">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 opacity-60" />}
      </div>
      <div className="font-heading text-xl font-medium mt-1">{value}</div>
    </div>
  );
}

export function Empty({ text, testid }) {
  return <div className="mt-4 rounded-2xl border border-dashed border-slate-200 py-14 text-center text-sm text-slate-400" data-testid={testid}>{text}</div>;
}

export function Table({ headers, children, bare, testid }) {
  const table = (
    <div className="overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
          <tr>{headers.map((h) => <th key={h} className={`px-5 py-3 font-semibold ${["Montant", "Coût", "Total TTC", "Total"].includes(h) ? "text-right" : "text-left"}`}>{h}</th>)}</tr>
        </thead>
        <tbody className="divide-y divide-slate-100">{children}</tbody>
      </table>
    </div>
  );
  if (bare) return table;
  return <div className="mt-4 rounded-2xl border border-slate-200 bg-white overflow-hidden">{table}</div>;
}
